import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PieChart from '../components/PieChart';
import BarChart from '../components/BarChart';
import VerticalBarChart from '../components/VerticalBarChart';
import { formatCurrency } from '../utils/formatCurrency';
import { roundMoney } from '../utils/money';
import { useVatMode } from '../hooks/useVatMode';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import CollapsibleSection from '../components/CollapsibleSection';
import ChartTimeRange, { getChartInitialRange } from '../components/ChartTimeRange';
import { apiFetch, API_BASE } from '../utils/api';
import HireDashboardTable from '../components/hires/HireDashboardTable';
import { useDepartment } from '../contexts/DepartmentContext';
import { STATUS_COLORS_HEX as STATUS_COLORS, STATUS_COLORS as STATUS_COLORS_TAILWIND, STATUS_LABELS, SUPPLIER_COLORS, USER_COLORS, SUB_DEPT_COLORS, HIRE_STATUS_COLORS, HIRE_STATUS_LABELS } from '../constants/colors';
import { ROLE_LABELS } from '../constants/statuses';
import { UserName } from '../components/InactiveBadge';
import EmptyState from '../components/EmptyState';

const DEFAULT_WIDGETS = {
  summaryCards: true,
  statusPieChart: true,
  spendOverTime: true,
  supplierSpendChart: true,
  userSpendChart: true,
  subDeptSpendChart: true,
  statusBreakdown: true,
  hireOverview: true,
};

const WIDGET_LABELS = {
  summaryCards: 'Summary Cards',
  statusPieChart: 'Order Status Chart',
  spendOverTime: 'Spend Over Time',
  supplierSpendChart: 'Spend by Supplier',
  userSpendChart: 'Spend by Team Member',
  subDeptSpendChart: 'Spend by Sub Department',
  statusBreakdown: 'Status Breakdown',
  hireOverview: 'Hire Overview',
};

const LS_KEY_WIDGETS = 'sfx_dept_dashboard_widgets';

function buildQS(range) {
  const params = new URLSearchParams();
  if (range && range.start_date) params.set('start_date', range.start_date);
  if (range && range.end_date) params.set('end_date', range.end_date);
  const qs = params.toString();
  return qs ? '?' + qs : '';
}

function DepartmentDashboard() {
  const navigate = useNavigate();
  const { formatMoney } = useVatMode();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const hasLoadedOnce = useRef(false);
  const [error, setError] = useState(null);
  const [supplierSpend, setSupplierSpend] = useState([]);
  const [spendOverTime, setSpendOverTime] = useState([]);
  const [subDeptSpend, setSubDeptSpend] = useState([]);
  const [spendPeriod, setSpendPeriod] = useState(() => {
    try { return localStorage.getItem('sfx_dept_dash_spend_period') || 'week'; } catch { return 'week'; }
  });
  const [showCustomize, setShowCustomize] = useState(false);
  const [widgets, setWidgets] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_KEY_WIDGETS);
      return saved ? { ...DEFAULT_WIDGETS, ...JSON.parse(saved) } : DEFAULT_WIDGETS;
    } catch {
      return DEFAULT_WIDGETS;
    }
  });
  const [sortField, setSortField] = useState('total_spend');
  const [sortDir, setSortDir] = useState('desc');

  // Hire data states
  const [hireItems, setHireItems] = useState([]);
  const [hireSummary, setHireSummary] = useState(null);
  const [hireTab, setHireTab] = useState('table');

  const currentUser = localStorage.getItem('sfx_requested_by') || '';
  const { activeRole, activeDepartment } = useDepartment();
  const userRole = activeRole || 'user';
  const userSubDepartment = localStorage.getItem('sfx_sub_department') || '';

  // Per-chart date ranges
  const [statusPieRange, setStatusPieRange] = useState(() => getChartInitialRange('dept_status_pie'));
  const [supplierChartRange, setSupplierChartRange] = useState(() => getChartInitialRange('dept_supplier_chart'));
  const [userChartRange, setUserChartRange] = useState(() => getChartInitialRange('dept_user_chart'));
  const [subDeptRange, setSubDeptRange] = useState(() => getChartInitialRange('dept_sub_dept'));
  const [spendOTRange, setSpendOTRange] = useState(() => getChartInitialRange('dept_spend_ot'));

  // Per-chart data states
  const [statusPieData, setStatusPieData] = useState(null);
  const [supplierChartData, setSupplierChartData] = useState(null);
  const [userChartData, setUserChartData] = useState(null);
  const [subDeptChartData, setSubDeptChartData] = useState(null);

  // Per-chart RANGED-fetch error flags (F6, silent-failure survey). A ranged
  // fetch failure must NEVER be answered by leaving the previous range's data
  // sitting in state under the new range's label — that is money attributed to
  // the wrong period and it looks entirely healthy. Each handler below sets its
  // flag on failure and clears it on success; the render gates on `!error`,
  // never on the data array, so a rejected/refused request always replaces the
  // chart with an explicit ErrorMessage instead of stale or falsely-empty bars.
  const [statusPieError, setStatusPieError] = useState(false);
  const [supplierChartError, setSupplierChartError] = useState(false);
  const [userChartError, setUserChartError] = useState(false);
  const [subDeptChartError, setSubDeptChartError] = useState(false);
  const [spendOverTimeError, setSpendOverTimeError] = useState(false);

  const fetchSpendOverTime = useCallback((range, period) => {
    if (!currentUser) return;
    const p = period || spendPeriod;
    const params = new URLSearchParams();
    if (range && range.start_date) params.set('start_date', range.start_date);
    if (range && range.end_date) params.set('end_date', range.end_date);
    params.set('period', p);
    setSpendOverTimeError(false);
    apiFetch(`${API_BASE}/api/dashboard/department/spend-over-time?` + params.toString())
      .then(res => {
        if (!res.ok) throw new Error('Failed to load spend-over-time data');
        return res.json();
      })
      .then(d => { setSpendOverTime(d.data || []); })
      .catch(() => { setSpendOverTime([]); setSpendOverTimeError(true); });
    // 🔀 **`activeDepartment?.department_id` IS PART OF THE REFETCH-ON-SWITCH FIX
    // (2026-08-18), THE `DepartmentOrders.js` 2026-08-17 PATTERN APPLIED HERE.**
    // `GET /api/dashboard/department/spend-over-time` is scoped SERVER-SIDE by
    // the `x-department-id` header (read from localStorage at CALL time), so the
    // request is always correctly scoped and only its TIMING could be wrong.
    // This callback is also invoked from inside `fetchDashboard` below, so this
    // dependency is what keeps THAT identity changing on a switch too — see the
    // comment there.
  }, [currentUser, spendPeriod, activeDepartment?.department_id]);

  const fetchDashboard = useCallback(() => {
    if (!currentUser) { setLoading(false); return; }
    // Summary cards show all-time figures (no date range filtering)
    if (!hasLoadedOnce.current) setLoading(true);
    setError(null);

    apiFetch(`${API_BASE}/api/dashboard/department`)
      .then(res => {
        if (!res.ok) throw new Error('Failed to load department dashboard');
        return res.json();
      })
      .then(d => { setData(d); setLoading(false); hasLoadedOnce.current = true; })
      .catch(err => { setError(err.message); setLoading(false); });

    apiFetch(`${API_BASE}/api/dashboard/department/supplier-spend`)
      .then(res => res.ok ? res.json() : [])
      .then(d => setSupplierSpend(d))
      .catch(() => setSupplierSpend([]));

    apiFetch(`${API_BASE}/api/dashboard/department/sub-department-spend`)
      .then(res => res.ok ? res.json() : [])
      .then(d => setSubDeptSpend(d))
      .catch(() => setSubDeptSpend([]));

    // Fetch hire data
    apiFetch(`${API_BASE}/api/hires`)
      .then(res => res.ok ? res.json() : { items: [] })
      .then(d => setHireItems(d.items || []))
      .catch(() => setHireItems([]));

    apiFetch(`${API_BASE}/api/hires/summary`)
      .then(res => res.ok ? res.json() : null)
      .then(d => setHireSummary(d))
      .catch(() => setHireSummary(null));

    fetchSpendOverTime();
    // 🔀 **`activeDepartment?.department_id` IS THE WHOLE OF THE REFETCH ON A
    // DEPARTMENT SWITCH (2026-08-18) — THE `DepartmentOrders.js` fetchOrders
    // FIX FROM 2026-08-17, APPLIED HERE.** Every request this function issues
    // is scoped SERVER-SIDE by the `x-department-id` header, which
    // `utils/api.js` reads from localStorage at CALL time — so the requests
    // are always correctly scoped and only their TIMING could be wrong.
    // `switchDepartment` is client-side and reloads nothing
    // (`contexts/DepartmentContext.js`), so without this dep the mount effect
    // below fires ONCE and the page keeps showing the PREVIOUS department's
    // figures until a full browser reload. Depending on the id changes this
    // callback's identity on a switch, which re-runs that effect.
    // 🪤 `department_id`, NEVER `.id` — `activeDepartment` never carries `id`.
    // The body does not read it directly (the header does, and
    // `fetchSpendOverTime` already carries its own copy of the same dep); it
    // is a dependency, not a reference — eslint-clean (an unused
    // member-expression dep is not flagged by exhaustive-deps).
  }, [currentUser, fetchSpendOverTime, activeDepartment?.department_id]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // 🔀 **CLEARS THE FOUR PER-CHART "RANGED" OVERRIDES ON A GENUINE DEPARTMENT
  // SWITCH (2026-08-18) — THE TWIN OF `DepartmentOrders.js`'s
  // `prevSwitchDeptRef` EFFECT THAT CLEARS `expandedOrders`/`orderDetails`.**
  // `statusPieData` / `supplierChartData` / `userChartData` / `subDeptChartData`
  // are populated ONLY by the `ChartTimeRange` handlers below
  // (`handleStatusPieRangeChange` etc.) — `fetchDashboard` never touches them.
  // So the dependency added above is not the WHOLE fix: it correctly refetches
  // `data`/`supplierSpend`/`subDeptSpend` (the ALL-TIME figures each chart
  // falls back to), but a chart that had a non-"All" range selected would go
  // on rendering the PREVIOUS department's ranged data forever — nothing ever
  // re-fetches or clears it on a switch. Clearing (not re-fetching) mirrors
  // `DepartmentOrders.js`: that effect does not try to re-fetch the newly
  // active department's copy of each expanded row either, it discards state
  // that belongs to the old context and lets the fresh top-level fetch (or,
  // here, the chart's own "All" fallback) take over.
  // 🪤 `useRef` seeded with the current id so this is a NO-OP on first render
  // (and on the initial undefined→id resolution): it fires ONLY on a genuine
  // change, so a single-department session never clears anything.
  const prevSwitchDeptRef = useRef(activeDepartment?.department_id);
  useEffect(() => {
    const deptId = activeDepartment?.department_id;
    if (prevSwitchDeptRef.current === deptId) return;
    prevSwitchDeptRef.current = deptId;
    setStatusPieData(null);
    setSupplierChartData(null);
    setUserChartData(null);
    setSubDeptChartData(null);
  }, [activeDepartment?.department_id]);

  const handleSpendPeriodChange = (period) => {
    setSpendPeriod(period);
    try { localStorage.setItem('sfx_dept_dash_spend_period', period); } catch {}
    fetchSpendOverTime(spendOTRange, period);
  };

  // Per-chart time range handlers
  const handleStatusPieRangeChange = (range) => {
    setStatusPieRange(range);
    setStatusPieError(false);
    apiFetch(`${API_BASE}/api/dashboard/department${buildQS(range)}`)
      .then(res => {
        if (!res.ok) throw new Error('Failed to load status breakdown');
        return res.json();
      })
      .then(d => { setStatusPieData(d.status_breakdown); })
      .catch(() => { setStatusPieError(true); });
  };

  const handleSupplierChartRangeChange = (range) => {
    setSupplierChartRange(range);
    setSupplierChartError(false);
    apiFetch(`${API_BASE}/api/dashboard/department/supplier-spend${buildQS(range)}`)
      .then(res => {
        if (!res.ok) throw new Error('Failed to load supplier spend');
        return res.json();
      })
      .then(d => setSupplierChartData(d))
      .catch(() => { setSupplierChartError(true); });
  };

  const handleUserChartRangeChange = (range) => {
    setUserChartRange(range);
    setUserChartError(false);
    apiFetch(`${API_BASE}/api/dashboard/department${buildQS(range)}`)
      .then(res => {
        if (!res.ok) throw new Error('Failed to load team member spend');
        return res.json();
      })
      .then(d => { setUserChartData(d.user_summaries); })
      .catch(() => { setUserChartError(true); });
  };

  const handleSubDeptRangeChange = (range) => {
    setSubDeptRange(range);
    setSubDeptChartError(false);
    apiFetch(`${API_BASE}/api/dashboard/department/sub-department-spend${buildQS(range)}`)
      .then(res => {
        if (!res.ok) throw new Error('Failed to load sub-department spend');
        return res.json();
      })
      .then(d => setSubDeptChartData(d))
      .catch(() => { setSubDeptChartError(true); });
  };

  const handleSpendOTRangeChange = (range) => {
    setSpendOTRange(range);
    fetchSpendOverTime(range);
  };

  const getStatusColor = (status) => {
    return STATUS_COLORS_TAILWIND[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  };

  const toggleWidget = (key) => {
    const updated = { ...widgets, [key]: !widgets[key] };
    setWidgets(updated);
    localStorage.setItem(LS_KEY_WIDGETS, JSON.stringify(updated));
  };

  const resetWidgets = () => {
    setWidgets(DEFAULT_WIDGETS);
    localStorage.setItem(LS_KEY_WIDGETS, JSON.stringify(DEFAULT_WIDGETS));
  };

  // Chart data getters
  const getStatusPieData = () => {
    const breakdown = statusPieData || (data && data.status_breakdown) || [];
    return breakdown.map(s => ({
      label: STATUS_LABELS[s.status] || s.status.charAt(0).toUpperCase() + s.status.slice(1),
      value: s.count,
      color: STATUS_COLORS[s.status] || '#9CA3AF'
    }));
  };

  const getSupplierBarData = () => {
    const source = supplierChartData || supplierSpend;
    const sorted = [...source].sort((a, b) => b.total_spend - a.total_spend);
    return sorted.map((s, i) => ({
      label: s.supplier_name,
      value: s.total_spend,
      color: SUPPLIER_COLORS[i % SUPPLIER_COLORS.length]
    }));
  };

  const getUserBarData = () => {
    const source = userChartData || (data && data.user_summaries) || [];
    const sorted = [...source].sort((a, b) => b.total_spend - a.total_spend);
    return sorted.map((s, i) => ({
      label: s.username,
      value: s.total_spend,
      color: USER_COLORS[i % USER_COLORS.length]
    }));
  };

  const getSubDeptPieData = () => {
    const source = subDeptChartData || subDeptSpend;
    if (!source || source.length === 0) return [];
    return source.map((s, i) => ({
      label: s.sub_department,
      value: Number(s.total_spend) || 0,
      color: SUB_DEPT_COLORS[i % SUB_DEPT_COLORS.length]
    }));
  };

  const getSpendOverTimeData = () => {
    const source = spendOverTime;
    if (!source || source.length === 0) return [];
    return source.map((row, index) => {
      let label = row.period_key;
      if (spendPeriod === 'week' && row.period_start) {
        const d = new Date(row.period_start + 'T00:00:00');
        if (!isNaN(d.getTime())) {
          label = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
        }
      } else if (spendPeriod === 'month' && row.period_key) {
        const parts = row.period_key.split('-');
        if (parts.length === 2) {
          const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, 1);
          if (!isNaN(d.getTime())) {
            label = d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
          }
        }
      }
      return {
        label,
        value: Number(row.total_spend) || 0,
        fullLabel: row.period_key,
        orderCount: row.order_count,
        isCurrent: index === source.length - 1
      };
    });
  };

  // Hire overview helpers
  const getHireStatusPieData = () => {
    if (!hireSummary) return [];
    const entries = [
      { key: 'on_hire', count: hireSummary.active_count },
      { key: 'overdue', count: hireSummary.overdue_count },
      { key: 'pending', count: hireSummary.upcoming_count },
      { key: 'off_hired', count: hireSummary.off_hired_count },
    ];
    return entries.filter(e => e.count > 0).map(e => ({
      label: HIRE_STATUS_LABELS[e.key],
      value: e.count,
      color: HIRE_STATUS_COLORS[e.key],
    }));
  };

  /**
   * 🔀 Owner-approved scope addition, 2026-09-01: reads `item.overdue_extra_cost`
   * (the whole-span delta, `hireOverrunDelta` in `server/helpers/hireCost.js`),
   * NOT `item.overrun_cost` (isolated-gap pricing) — see the identical fix and
   * its docblock in `HireDashboardTable.js`'s "Overrun" column for the money
   * defect this closes (a weekly line late but still inside its already-paid
   * week billed a whole extra week under the old figure).
   *
   * 🚨 GATED ON `hire_status === 'overdue'`, EXPLICITLY — not just a sign
   * check. `overdue_extra_cost` is NEGATIVE for every pending/on-hire row
   * (`utils/hireOverdueSignal.js`'s trap docblock), and this widget SORTS
   * across every hire item in the department, so a bare `> 0` filter is not
   * enough defence in depth here the way it is on a single cell. The status
   * gate is the SAME instrument `hireOverdueSignalLabel` uses, applied here
   * because this list ranks rather than merely displaying one row.
   *
   * 🔀 Deliberately NARROWER than `HireDashboardTable`'s "Overrun" column,
   * which still shows a real extra cost for an `off_hired` item returned
   * late — this chart answers "what needs attention right now", so a closed
   * hire does not appear here even though its historical cost is real.
   */
  const getTopOverrunItems = () => {
    if (!hireItems || hireItems.length === 0) return [];
    return [...hireItems]
      .filter(i => i.hire_status === 'overdue' && Number.isFinite(i.overdue_extra_cost) && i.overdue_extra_cost > 0)
      .sort((a, b) => b.overdue_extra_cost - a.overdue_extra_cost)
      .slice(0, 10)
      .map((item, idx) => ({
        label: item.description || `Item #${item.item_id}`,
        value: item.overdue_extra_cost,
        color: ['#EF4444', '#F97316', '#F59E0B', '#FB923C', '#DC2626', '#EA580C', '#D97706', '#C2410C', '#B91C1C', '#A16207'][idx % 10],
      }));
  };

  // 🚨 QUANTITY MULTIPLIES THIS TOO (2026-08-20) — and this surface AGREED with
  // `line_total` before that date and disagreed AFTER it, which is what puts it
  // in scope for the quantity hotfix rather than on the backlog. The three rate
  // columns are PER-ITEM rates (see `hireLineQuantity` in `utils/hireCost.js`
  // for the ruling), so a 3 × £50/day line burns £1,050/wk, not £350/wk. Left
  // unmultiplied it read £350 beside a `HireDashboardTable` on the SAME PAGE —
  // fed by `/api/hires`, which does carry quantity — showing £3,150.
  //
  // 🔴 DELIBERATELY NOT DELEGATED TO `utils/hireCost.js`, AND DO NOT "FINISH"
  // THAT REFACTOR HERE. A weekly burn RATE is not a line TOTAL: `hireCostForDays`
  // prices a FIXED span (with a minimum-one-day floor and a `Math.ceil(days/7)`
  // week count), whereas this annualises an in-flight hire to "what is this
  // costing me per week right now" and deliberately contributes NOTHING for the
  // `total` rate type, which has no meaningful weekly rate. Unifying them would
  // change the number on screen and needs a product decision, not a tidy-up.
  // Backlogged; only the quantity multiplier is fixed here.
  const getWeeklyBurnRate = () => {
    if (!hireItems || hireItems.length === 0) return 0;
    let total = 0;
    for (const item of hireItems) {
      if (item.hire_status !== 'on_hire' && item.hire_status !== 'overdue') continue;
      // Same rule as `hireLineQuantity`: coerce first and test `> 0`, never a
      // bare `||` — quantity arrives as a STRING from the API and `'0'` is
      // TRUTHY, and 0/negative/unparseable all mean "one of it", never zero.
      const parsedQty = Number(item.quantity);
      const qty = Number.isFinite(parsedQty) && parsedQty > 0 ? parsedQty : 1;
      if (item.hire_rate_type === 'daily' && item.daily_rate) {
        total += item.daily_rate * 7 * qty;
      } else if (item.hire_rate_type === 'weekly' && item.weekly_rate) {
        total += item.weekly_rate * qty;
      }
      // 'total' rate type doesn't have a meaningful weekly rate
    }
    // 🔀 2026-09-08 (money.js T7). This was an inline `Math.round(total * 100) / 100`
    // — the LAST old-rule (Rule B) money round left on the client — and it is now
    // the shared primitive. It is a DISPLAY figure and nothing here is persisted:
    // the server never sees this number.
    //
    // 🟢 THE MOVEMENT IS MEASURED, NOT ASSUMED, and on every total this tile can
    // build from 2dp rates it is ZERO: 2,000,010 combinations (every penny rate
    // from £0.00 to £2,000.00 x quantities 1/2/3/7/10 x both branch shapes,
    // `daily_rate * 7 * qty` and `weekly_rate * qty`) are byte-identical under
    // the two rules. The two differ only on a sub-£4 `.xx5` — 61 values in the
    // whole £0.005–£999.995 half-penny grid, the largest £3.785, every one moving
    // UP by exactly £0.01 — because `4 + Number.EPSILON === 4` absorbs the nudge
    // from £4 up. Reachable here only through a rate carrying more than 2dp
    // (0.5025/day x 2 is exactly £1.005, which now reads £1.01 rather than £1.00).
    //
    // 🪤 ONE OTHER DIFFERENCE, CHECKED RATHER THAN ASSUMED SAFE: `roundMoney`
    // answers `null` where the inline rule answered `NaN` (an unparseable
    // `daily_rate` string that is nonetheless truthy). Both render identically —
    // `formatMoney` -> `formatCurrency` does `Number(amount || 0)`, and NaN and
    // null are both falsy — so the tile still shows £0.00 either way.
    //
    // 🔴 STILL DELIBERATELY NOT DELEGATED TO `utils/hireCost.js` — see the note
    // above this function. Sharing the ROUNDER is not sharing the RULE.
    return roundMoney(total);
  };

  // Sort user summaries
  const getSortedUserSummaries = () => {
    if (!data || !data.user_summaries) return [];
    return [...data.user_summaries].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (typeof aVal === 'string') return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const roleLabel = ROLE_LABELS[userRole] || userRole;

  if (loading) {
    return (
      <div className="max-w-full p-4 lg:p-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <LoadingSpinner message="Loading department dashboard..." />
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="max-w-full p-4 lg:p-6">
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <p className="text-gray-600">Please select a user account to view department dashboard.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-full p-4 lg:p-6">
        <ErrorMessage error={error} onRetry={fetchDashboard} title="Failed to load department dashboard" />
      </div>
    );
  }

  const hasNoData = data && (!data.status_breakdown || data.status_breakdown.length === 0) && !data.total_spend && !data.total_orders;

  return (
    <div className="max-w-full p-2.5">
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="text-sm text-gray-500 mt-0.5">
            Team overview for <span className="font-medium text-gray-700">{currentUser}</span>
            <span className="ml-1.5 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">{roleLabel}</span>
            {userSubDepartment && <span className="ml-1 text-gray-700"> - {userSubDepartment}</span>}
          </p>
        </div>
        <button
          onClick={() => setShowCustomize(!showCustomize)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition-colors"
          title="Customize dashboard"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Customize
        </button>
      </div>

      {/* Customize Panel */}
      {showCustomize && (
        <div className="bg-white rounded-lg shadow-md p-4 mb-4 border border-blue-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700">Customize Dashboard Widgets</h3>
            <button onClick={resetWidgets} className="text-xs text-blue-600 hover:text-blue-800">
              Reset to Default
            </button>
          </div>
          <div className="flex flex-wrap gap-3">
            {Object.entries(WIDGET_LABELS).map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={widgets[key]}
                  onChange={() => toggleWidget(key)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-600">{label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {hasNoData ? (
        <div className="mt-4">
          <EmptyState
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-14 w-14 mx-auto text-gray-300 dark:text-gray-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            }
            title="No team orders yet"
            description="Orders submitted by your team will appear here. Submit one to get the ball rolling."
            cta={{ label: 'Create an order', to: '/orders/new' }}
          />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          {/* 🎨 Tile text is ONE pair per hue — -700 light / -300 dark (2026-10-01
              contrast pass). The -400/-500 captions measured 1.6–2.43:1 on the
              tile's bg-gray-50; -700 is 4.7–6.4:1 there, and -300 is what
              index.css's html.dark !important overrides already paint for the
              green/blue/yellow -700 classes, so dark is 8.1–11.1:1 on #1e293b.
              The captions carry print:hidden because index.css's print block
              used to hide them by their OLD colour classes. */}
          {widgets.summaryCards && data && (
            <CollapsibleSection id="dept-summary-cards" title="Team Summary" className="mb-6">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 md:gap-4 p-3 md:p-4">
                <div className="bg-gray-50 rounded-lg p-3 md:p-5 cursor-pointer hover:bg-green-50 hover:ring-2 hover:ring-green-300 transition-all"
                  onClick={() => navigate('/department/orders')} title="View department orders">
                  <p className="text-xs md:text-sm text-gray-500 mb-1">Team Spend</p>
                  <p className="text-xl md:text-base md:text-2xl font-bold truncate text-green-700 dark:text-green-300 truncate" title={formatMoney(data.total_spend)}>{formatMoney(data.total_spend)}</p>
                  <p className="text-xs text-green-700 dark:text-green-300 mt-1 print:hidden">Approved/ordered/delivered</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 md:p-5 cursor-pointer hover:bg-blue-50 hover:ring-2 hover:ring-blue-300 transition-all"
                  onClick={() => navigate('/department/orders')} title="View department orders">
                  <p className="text-xs md:text-sm text-gray-500 mb-1">Total Orders</p>
                  <p className="text-xl md:text-base md:text-2xl font-bold truncate text-blue-700 dark:text-blue-300">{data.total_orders}</p>
                  <p className="text-xs text-blue-700 dark:text-blue-300 mt-1 print:hidden">All team orders</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 md:p-5 cursor-pointer hover:bg-amber-50 hover:ring-2 hover:ring-amber-300 transition-all"
                  onClick={() => navigate('/department/orders')} title="View outstanding orders">
                  <p className="text-xs md:text-sm text-gray-500 mb-1">Outstanding</p>
                  <p className="text-xl md:text-base md:text-2xl font-bold truncate text-amber-700 dark:text-amber-300">{data.outstanding_count}</p>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-1 print:hidden">Not yet delivered</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 md:p-5 cursor-pointer hover:bg-yellow-50 hover:ring-2 hover:ring-yellow-300 transition-all"
                  onClick={() => navigate('/approvals')} title="View approvals">
                  <p className="text-xs md:text-sm text-gray-500 mb-1">Awaiting Approval</p>
                  <p className="text-xl md:text-base md:text-2xl font-bold truncate text-yellow-700 dark:text-yellow-300">{data.unapproved_count}</p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1 print:hidden">Pending approval</p>
                </div>
              </div>
            </CollapsibleSection>
          )}

          {/* Charts Row - Pie + Supplier side by side on desktop */}
          {(widgets.statusPieChart || widgets.supplierSpendChart) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 items-start">
              {widgets.statusPieChart && (
                <CollapsibleSection id="dept-status-pie" title="Order Status Distribution">
                  <div className="p-5">
                    <ChartTimeRange chartId="dept_status_pie" onChange={handleStatusPieRangeChange} className="mb-3" />
                    {statusPieError ? (
                      <ErrorMessage error="Failed to load order status data for the selected range" onRetry={() => handleStatusPieRangeChange(statusPieRange)} />
                    ) : (
                      <PieChart
                        data={getStatusPieData()}
                        size={200}
                      />
                    )}
                  </div>
                </CollapsibleSection>
              )}

              {widgets.supplierSpendChart && (
                <CollapsibleSection id="dept-supplier-chart" title="Team Spend by Supplier">
                  <div className="p-5">
                    <ChartTimeRange chartId="dept_supplier_chart" onChange={handleSupplierChartRangeChange} className="mb-3" />
                    {supplierChartError ? (
                      <ErrorMessage error="Failed to load supplier spend for the selected range" onRetry={() => handleSupplierChartRangeChange(supplierChartRange)} />
                    ) : (
                      <BarChart
                        data={getSupplierBarData()}
                        valuePrefix={'\u00A3'}
                        maxBars={8}
                      />
                    )}
                  </div>
                </CollapsibleSection>
              )}
            </div>
          )}

          {/* Team Member Spend + Sub Department side by side */}
          {(widgets.userSpendChart || widgets.subDeptSpendChart) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 items-start">
              {widgets.userSpendChart && (
                <CollapsibleSection id="dept-user-chart" title="Spend by Team Member">
                  <div className="p-5">
                    <ChartTimeRange chartId="dept_user_chart" onChange={handleUserChartRangeChange} className="mb-3" />
                    {userChartError ? (
                      <ErrorMessage error="Failed to load team member spend for the selected range" onRetry={() => handleUserChartRangeChange(userChartRange)} />
                    ) : (
                      <BarChart
                        data={getUserBarData()}
                        valuePrefix={'\u00A3'}
                        maxBars={10}
                      />
                    )}
                  </div>
                </CollapsibleSection>
              )}

              {widgets.subDeptSpendChart && subDeptSpend.length > 0 && (
                <CollapsibleSection id="dept-sub-dept-spend" title="Spend by Sub Department">
                  <div className="p-5">
                    <ChartTimeRange chartId="dept_sub_dept" onChange={handleSubDeptRangeChange} className="mb-3" />
                    {subDeptChartError ? (
                      <ErrorMessage error="Failed to load sub-department spend for the selected range" onRetry={() => handleSubDeptRangeChange(subDeptRange)} />
                    ) : (
                      <PieChart
                        data={getSubDeptPieData()}
                        size={220}
                        valueFormatter={formatCurrency}
                      />
                    )}
                  </div>
                </CollapsibleSection>
              )}
            </div>
          )}

          {/* Spend Over Time Chart */}
          {widgets.spendOverTime && (
            <CollapsibleSection id="dept-spend-over-time" title="Team Spend Over Time" className="mb-6">
              <div className="p-5">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="text-sm text-gray-500 mr-1">View by:</span>
                  <button
                    onClick={() => handleSpendPeriodChange('week')}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      spendPeriod === 'week' ? 'bg-blue-600 dark:bg-blue-500/70 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    Week
                  </button>
                  <button
                    onClick={() => handleSpendPeriodChange('month')}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      spendPeriod === 'month' ? 'bg-blue-600 dark:bg-blue-500/70 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    Month
                  </button>
                </div>
                {spendOverTimeError ? (
                  <ErrorMessage error="Failed to load spend over time" onRetry={() => fetchSpendOverTime(spendOTRange, spendPeriod)} />
                ) : (
                  <VerticalBarChart
                    data={getSpendOverTimeData()}
                    valuePrefix={'\u00A3'}
                    color="#3B82F6"
                    height={280}
                  />
                )}
              </div>
            </CollapsibleSection>
          )}

          {/* Status Breakdown */}
          {widgets.statusBreakdown && data && data.status_breakdown && data.status_breakdown.length > 0 && (
            <CollapsibleSection id="dept-status-breakdown" title="Order Status Breakdown" className="mb-6">
              <div className="p-5">
                <div className="flex flex-wrap gap-3">
                  {data.status_breakdown.map(s => (
                    <button
                      key={s.status}
                      onClick={() => navigate('/department/orders')}
                      className={`px-3 py-1 rounded-full text-sm font-medium cursor-pointer hover:opacity-80 hover:ring-2 hover:ring-offset-1 hover:ring-gray-400 transition ${getStatusColor(s.status)}`}
                    >
                      {STATUS_LABELS[s.status] || s.status.charAt(0).toUpperCase() + s.status.slice(1)}: {s.count}
                    </button>
                  ))}
                </div>
              </div>
            </CollapsibleSection>
          )}

          {/* Hire Overview */}
          {widgets.hireOverview && (
            <CollapsibleSection id="dept-hire-overview" title="Hire Overview" className="mb-6">
              <div className="p-5">
                {/* Summary Cards Row */}
                {hireSummary && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
                    <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-3 text-center">
                      <div className="text-base md:text-2xl font-bold truncate text-blue-600 dark:text-blue-400">{hireSummary.active_count || 0}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">Active</div>
                    </div>
                    <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg p-3 text-center">
                      <div className="text-base md:text-2xl font-bold truncate text-red-600 dark:text-red-400">{hireSummary.overdue_count || 0}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">Overdue</div>
                    </div>
                    <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg p-3 text-center">
                      <div className="text-base md:text-2xl font-bold truncate text-amber-600 dark:text-amber-400">{hireSummary.upcoming_count || 0}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">Upcoming</div>
                    </div>
                    <div className="bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-700 rounded-lg p-3 text-center">
                      <div className="text-base md:text-2xl font-bold truncate text-purple-600 dark:text-purple-400">{formatMoney(getWeeklyBurnRate())}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">Weekly Burn</div>
                    </div>
                    <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg p-3 text-center">
                      <div className="text-base md:text-2xl font-bold truncate text-green-600 dark:text-green-400">{formatMoney(hireSummary.current_total || 0)}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">Total Spend</div>
                    </div>
                    <div className={`border rounded-lg p-3 text-center ${
                      (hireSummary.variance || 0) > 0
                        ? 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700'
                        : (hireSummary.variance || 0) < 0
                          ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700'
                          : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600'
                    }`}>
                      <div className={`text-base md:text-2xl font-bold truncate ${
                        (hireSummary.variance || 0) > 0
                          ? 'text-red-600 dark:text-red-400'
                          : (hireSummary.variance || 0) < 0
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-gray-600 dark:text-gray-400'
                      }`}>
                        {(hireSummary.variance || 0) > 0 ? '+' : ''}{formatMoney(Math.abs(hireSummary.variance || 0))}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">Variance</div>
                    </div>
                  </div>
                )}

                {/* Tab Toggle */}
                <div className="flex gap-1 mb-4 bg-gray-100 dark:bg-gray-700 rounded-lg p-1 w-fit">
                  <button
                    onClick={() => setHireTab('table')}
                    className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      hireTab === 'table'
                        ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    Table
                  </button>
                  <button
                    onClick={() => setHireTab('charts')}
                    className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      hireTab === 'charts'
                        ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    Charts
                  </button>
                </div>

                {/* Table View */}
                {hireTab === 'table' && (
                  <HireDashboardTable items={hireItems} />
                )}

                {/* Chart View */}
                {hireTab === 'charts' && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-3">Hire Status Distribution</h4>
                      <PieChart
                        data={getHireStatusPieData()}
                        size={200}
                      />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-3">Top Overrun Items</h4>
                      <BarChart
                        data={getTopOverrunItems()}
                        valuePrefix={'\u00A3'}
                        maxBars={10}
                      />
                    </div>
                  </div>
                )}
              </div>
            </CollapsibleSection>
          )}

          {/* User Spend Table */}
          {data && data.user_summaries && data.user_summaries.length > 0 && (
            <CollapsibleSection id="dept-user-table" title="Team Member Breakdown" className="mb-6">
              <div className="p-5 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 font-medium text-gray-600 cursor-pointer hover:text-gray-900"
                        onClick={() => handleSort('username')}>
                        Team Member {sortField === 'username' ? (sortDir === 'asc' ? '\u25B2' : '\u25BC') : ''}
                      </th>
                      <th className="text-right py-2 px-3 font-medium text-gray-600 cursor-pointer hover:text-gray-900"
                        onClick={() => handleSort('order_count')}>
                        Orders {sortField === 'order_count' ? (sortDir === 'asc' ? '\u25B2' : '\u25BC') : ''}
                      </th>
                      <th className="text-right py-2 px-3 font-medium text-gray-600 cursor-pointer hover:text-gray-900"
                        onClick={() => handleSort('total_spend')}>
                        Total Spend {sortField === 'total_spend' ? (sortDir === 'asc' ? '\u25B2' : '\u25BC') : ''}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {getSortedUserSummaries().map((u, i) => (
                      <tr key={u.username} className={`border-b border-gray-100 ${i % 2 === 0 ? 'bg-gray-50' : ''}`}>
                        <td className="py-2 px-3 font-medium text-gray-800"><UserName name={u.username} isActive={u.is_active} /></td>
                        <td className="py-2 px-3 text-right text-gray-600">{u.order_count}</td>
                        <td className="py-2 px-3 text-right font-medium text-green-600">{formatMoney(u.total_spend)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CollapsibleSection>
          )}
        </>
      )}
    </div>
  );
}

export default DepartmentDashboard;
